import { useMemo, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { LANGUAGES, type Language } from '../data/api';

/* ==========================================================================
 *  Code rendering
 *
 *  A deliberately small highlighter rather than a syntax-highlighting library:
 *  the docs only ever show JSON and four well-behaved snippet languages, and
 *  shipping 200 KB of grammar to colour a dozen code blocks is not a trade
 *  worth making. Strings, numbers, comments and keys is all the signal needed.
 * ========================================================================== */

type Token = { text: string; kind: 'plain' | 'string' | 'number' | 'comment' | 'key' | 'keyword' };

const KEYWORDS =
  /\b(?:const|let|await|async|import|from|export|function|return|if|else|switch|case|break|new|throw|try|except|raise|def|print|echo|foreach|require|use|class|public|static|null|true|false|None|True|False)\b/;

function tokenize(source: string, json: boolean): Token[] {
  const tokens: Token[] = [];
  // One pass, ordered so a comment marker inside a string never wins.
  const pattern = json
    ? /("(?:[^"\\]|\\.)*"\s*:)|("(?:[^"\\]|\\.)*")|(-?\d+(?:\.\d+)?)/g
    : /(#[^\n]*|\/\/[^\n]*)|('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")|(-?\b\d+(?:\.\d+)?\b)/g;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(source)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ text: source.slice(lastIndex, match.index), kind: 'plain' });
    }
    if (json) {
      if (match[1]) tokens.push({ text: match[1], kind: 'key' });
      else if (match[2]) tokens.push({ text: match[2], kind: 'string' });
      else tokens.push({ text: match[3]!, kind: 'number' });
    } else {
      if (match[1]) tokens.push({ text: match[1], kind: 'comment' });
      else if (match[2]) tokens.push({ text: match[2], kind: 'string' });
      else tokens.push({ text: match[3]!, kind: 'number' });
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < source.length) tokens.push({ text: source.slice(lastIndex), kind: 'plain' });

  // Keywords are only picked out of plain runs, so they cannot appear inside
  // a string literal.
  if (json) return tokens;
  return tokens.flatMap((token) => {
    if (token.kind !== 'plain') return token;
    const parts: Token[] = [];
    let rest = token.text;
    let m: RegExpExecArray | null;
    const kw = new RegExp(KEYWORDS.source, 'g');
    let cursor = 0;
    while ((m = kw.exec(rest)) !== null) {
      if (m.index > cursor) parts.push({ text: rest.slice(cursor, m.index), kind: 'plain' });
      parts.push({ text: m[0], kind: 'keyword' });
      cursor = kw.lastIndex;
    }
    if (cursor < rest.length) parts.push({ text: rest.slice(cursor), kind: 'plain' });
    return parts.length ? parts : token;
  });
}

const TOKEN_CLASS: Record<Token['kind'], string> = {
  plain: 'text-content-muted',
  string: 'text-brand-300',
  number: 'text-[#7DD3FC]',
  comment: 'text-content-faint italic',
  key: 'text-content',
  keyword: 'text-[#C4B5FD]',
};

export function Code({ source, json = false }: { source: string; json?: boolean }) {
  const tokens = useMemo(() => tokenize(source, json), [source, json]);
  return (
    <pre className="scrollbar-thin overflow-x-auto px-4 py-3.5 text-xs leading-relaxed">
      <code className="font-mono">
        {tokens.map((token, i) => (
          <span key={i} className={TOKEN_CLASS[token.kind]}>
            {token.text}
          </span>
        ))}
      </code>
    </pre>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1600);
        } catch {
          // Clipboard is unavailable over plain HTTP and in some embedded
          // views; the code is still selectable, so fail quietly.
        }
      }}
      className="btn btn-ghost h-7 gap-1.5 px-2 text-2xs"
      aria-label="Copy code"
    >
      {copied ? (
        <>
          <Check className="h-3 w-3 text-brand-400" aria-hidden="true" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" aria-hidden="true" />
          Copy
        </>
      )}
    </button>
  );
}

/** A single code panel with an optional caption. */
export function CodePanel({ title, source, json }: { title: string; source: string; json?: boolean }) {
  return (
    <div className="panel overflow-hidden">
      <div className="flex items-center justify-between border-b border-line bg-surface-overlay px-3 py-1.5">
        <span className="text-2xs font-medium uppercase tracking-wider text-content-subtle">{title}</span>
        <CopyButton value={source} />
      </div>
      <Code source={source} json={json} />
    </div>
  );
}

/**
 * The four-language switcher. The chosen language is lifted to the page so
 * every block on the page switches together — a reader working in Python
 * should never have to re-pick it at each endpoint.
 */
export function LanguageTabs({
  snippets,
  language,
  onLanguageChange,
  title = 'Request',
}: {
  snippets: Record<Language, string>;
  language: Language;
  onLanguageChange: (language: Language) => void;
  title?: string;
}) {
  return (
    <div className="panel overflow-hidden">
      <div className="flex items-center justify-between border-b border-line bg-surface-overlay pr-2">
        <div role="tablist" aria-label="Code language" className="flex">
          {LANGUAGES.map((lang) => {
            const isActive = lang.id === language;
            return (
              <button
                key={lang.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => onLanguageChange(lang.id)}
                className={`relative px-3 py-2 text-2xs font-medium transition-colors focus:outline-none focus-visible:shadow-focus-brand ${
                  isActive ? 'text-content' : 'text-content-subtle hover:text-content-muted'
                }`}
              >
                {lang.label}
                {isActive && <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-brand-500" />}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1">
          <span className="hidden text-2xs uppercase tracking-wider text-content-faint sm:inline">{title}</span>
          <CopyButton value={snippets[language]} />
        </div>
      </div>
      <Code source={snippets[language]} />
    </div>
  );
}
